import React, { useEffect, useState } from 'react';
import {
    XCircle,
    CheckCircle,
    Calendar,
    Clock,
    Phone,
    User,
    Hash,
    AlertCircle,
    RefreshCw,
    PlusCircle,
    Loader2
} from 'lucide-react';
import { useWebSocket } from '../../websocket/WebSocketContext';
import { sendWhatsAppNotification } from './whatsappConfigService';

export default function WhatsAppDetails({ selectedWp, onClose, setError, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const [isResending, setIsResending] = useState(false);
    const [actionSuccess, setActionSuccess] = useState('');
    const [actionError, setActionError] = useState('');

    useEffect(() => {
        // Reset state whenever a new record is selected
        if (!selectedWp) return;
        setActionSuccess('');
        setActionError('');
        setIsResending(false);
    }, [selectedWp]);

    if (!selectedWp) return null;

    const getStatusColor = (status, wpResponse) => {
        const resp = wpResponse?.responseCode?.toLowerCase() || '';
        if (status === 'SUCCESS' || resp.includes('success')) return 'text-green-400';
        if (status === 'FAILED' || resp.includes('error') || resp.includes('fail')) return 'text-red-400';
        return 'text-yellow-400';
    };

    const getStatusIcon = (status, wpResponse) => {
        const resp = wpResponse?.responseCode?.toLowerCase() || '';
        if (status === 'SUCCESS' || resp.includes('success')) return CheckCircle;
        if (status === 'FAILED' || resp.includes('error') || resp.includes('fail')) return XCircle;
        return AlertCircle;
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        if (phone.length === 10) return phone.replace(/(\d{5})(\d{5})/, '$1 $2');
        return phone;
    };

    const handleResend = async () => {
        setIsResending(true);
        setActionSuccess('');
        setActionError('');

        const payload = {
            Type: selectedWp.Type || 'SINGLE',
            mobileNo: selectedWp.mobileNo,
            campaignId: selectedWp.campaignId,
            appName: selectedWp.appName,
            attributeDetails: selectedWp.attributeDetails || [],
        };

        try {
            await sendWhatsAppNotification({ deliveryMode, client, loginId }, payload);
            setActionSuccess('WhatsApp message resent successfully!');
            setTimeout(() => setActionSuccess(''), 3000);
        } catch (err) {
            console.error('Error resending WhatsApp:', err);
            setActionError(`Failed to resend: ${err.message || 'Unknown error'}`);
            setTimeout(() => setActionError(''), 3000);
        } finally {
            setIsResending(false);
        }
    };

    const StatusIcon = getStatusIcon(selectedWp.STATUS, selectedWp.wp_response);
    const statusColorClass = getStatusColor(selectedWp.STATUS, selectedWp.wp_response);

    const showResendButton = selectedWp.STATUS === 'FAILED';
    const showQueueButton = selectedWp.STATUS === 'PENDING';

    // Parse response string if present e.g. "9718688183~90041193~~20260221192400~201479~..."
    const parseWpResponseString = (str) => {
        if (!str) return null;
        const parts = str.split('~');
        if (parts.length < 4) return null;
        return {
            mobile: parts[0],
            campaignId: parts[1],
            extra: parts[2],
            timestamp: parts[3],
            ref: parts[4],
            email: parts[5],
        };
    };

    const parsedWpResponse = parseWpResponseString(selectedWp.wp_response?.response);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-white">WhatsApp Details</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                    >
                        <XCircle className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Action Feedback */}
                {(actionSuccess || actionError) && (
                    <div className={`mb-4 p-4 rounded-xl flex items-center space-x-2 ${actionSuccess
                        ? 'bg-green-500/20 border border-green-500/50 text-green-200'
                        : 'bg-red-500/20 border border-red-500/50 text-red-200'
                        }`}>
                        {actionSuccess ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span>{actionSuccess || actionError}</span>
                    </div>
                )}

                {/* Resend / Queue Buttons */}
                {(showResendButton || showQueueButton) && (
                    <div className="mb-6 flex gap-3">
                        {showResendButton && (
                            <button
                                onClick={handleResend}
                                disabled={isResending}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-orange-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResending ? (
                                    <><Loader2 className="w-5 h-5 animate-spin" /><span>Resending...</span></>
                                ) : (
                                    <><RefreshCw className="w-5 h-5" /><span>Resend WhatsApp</span></>
                                )}
                            </button>
                        )}
                        {showQueueButton && (
                            <button
                                onClick={handleResend}
                                disabled={isResending}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResending ? (
                                    <><Loader2 className="w-5 h-5 animate-spin" /><span>Adding to Queue...</span></>
                                ) : (
                                    <><PlusCircle className="w-5 h-5" /><span>Add to Queue</span></>
                                )}
                            </button>
                        )}
                    </div>
                )}

                <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">ID</p>
                            <p className="text-white text-sm font-mono break-all">{selectedWp._id}</p>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Status</p>
                            <div className="flex items-center space-x-2">
                                {React.createElement(StatusIcon, { className: `w-4 h-4 ${statusColorClass}` })}
                                <span className={`text-sm font-medium ${statusColorClass}`}>{selectedWp.STATUS}</span>
                            </div>
                        </div>

                        {selectedWp.rf1_id && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site ID</p>
                                <p className="text-white text-sm font-mono break-all">{selectedWp.rf1_id}</p>
                            </div>
                        )}
                        {selectedWp.rf1_name && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site Name</p>
                                <p className="text-white text-sm font-mono break-all">{selectedWp.rf1_name}</p>
                            </div>
                        )}
                        {selectedWp.rf2_id && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location ID</p>
                                <p className="text-white text-sm font-mono break-all">{selectedWp.rf2_id}</p>
                            </div>
                        )}
                        {selectedWp.rf2_name && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location Name</p>
                                <p className="text-white text-sm font-mono break-all">{selectedWp.rf2_name}</p>
                            </div>
                        )}

                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Type</p>
                            <span className="px-2 py-1 bg-teal-500/20 text-teal-200 text-xs rounded-full">
                                {selectedWp.Type || 'SINGLE'}
                            </span>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Vendor</p>
                            <p className="text-white">{selectedWp.vendorName || 'N/A'}</p>
                        </div>
                    </div>

                    {/* Message Info */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
                        <p className="text-white/60 text-sm font-semibold uppercase tracking-wide">Message Info</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                <div className="flex items-center space-x-2">
                                    <Phone className="w-4 h-4 text-white/60" />
                                    <p className="text-white font-mono">{formatPhoneNumber(selectedWp.mobileNo) || 'N/A'}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                <div className="flex items-center space-x-2">
                                    <User className="w-4 h-4 text-white/60" />
                                    <p className="text-white">{selectedWp.appName || 'N/A'}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Campaign ID</p>
                                <div className="flex items-center space-x-2">
                                    <Hash className="w-4 h-4 text-white/60" />
                                    <p className="text-white font-mono">{selectedWp.campaignId || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Attribute Details */}
                        {selectedWp.attributeDetails && selectedWp.attributeDetails.length > 0 && (
                            <div>
                                <p className="text-white/40 text-xs uppercase tracking-wide mb-2">Attribute Details</p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedWp.attributeDetails.map((attr, i) => (
                                        <div key={i} className="px-3 py-1 bg-teal-500/10 border border-teal-500/20 rounded-lg text-sm">
                                            <span className="text-white/50">{attr.attributeName}:</span>{' '}
                                            <span className="text-white font-medium">{attr.attributeValue}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* WhatsApp Response */}
                    {selectedWp.wp_response && (
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-3">WhatsApp Response</p>
                            <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20 rounded-xl p-4 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Status Code</p>
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${selectedWp.wp_response.statusCode === 200
                                            ? 'bg-green-500/20 text-green-200'
                                            : 'bg-red-500/20 text-red-200'
                                            }`}>
                                            {selectedWp.wp_response.statusCode}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Response Code</p>
                                        <p className="text-white text-sm">{selectedWp.wp_response.responseCode || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Parsed response string */}
                                {parsedWpResponse && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/10">
                                        {parsedWpResponse.mobile && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Confirmed Mobile</p>
                                                <div className="flex items-center space-x-2">
                                                    <Phone className="w-4 h-4 text-teal-400" />
                                                    <p className="text-white font-mono text-sm">{parsedWpResponse.mobile}</p>
                                                </div>
                                            </div>
                                        )}
                                        {parsedWpResponse.campaignId && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Confirmed Campaign</p>
                                                <p className="text-white font-mono text-sm">{parsedWpResponse.campaignId}</p>
                                            </div>
                                        )}
                                        {parsedWpResponse.timestamp && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Delivery Timestamp</p>
                                                <div className="flex items-center space-x-2">
                                                    <Clock className="w-4 h-4 text-teal-400" />
                                                    <p className="text-white text-sm">{parsedWpResponse.timestamp}</p>
                                                </div>
                                            </div>
                                        )}
                                        {parsedWpResponse.ref && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Reference</p>
                                                <p className="text-white font-mono text-sm">{parsedWpResponse.ref}</p>
                                            </div>
                                        )}
                                        {parsedWpResponse.email && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Email / ID</p>
                                                <p className="text-white font-mono text-sm break-all">{parsedWpResponse.email}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Raw response */}
                                {selectedWp.wp_response.response && (
                                    <div className="pt-2 border-t border-white/10">
                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Raw Response</p>
                                        <div className="bg-white/10 p-3 rounded-lg">
                                            <p className="text-white font-mono text-xs break-all">
                                                {selectedWp.wp_response.response}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Timestamps */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Date</p>
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedWp.date}</p>
                            </div>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Creation Time</p>
                            <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedWp.creation_time_str}</p>
                            </div>
                        </div>
                        {selectedWp.last_update_time && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Last Updated</p>
                                <div className="flex items-center space-x-2">
                                    <Clock className="w-4 h-4 text-white/60" />
                                    <p className="text-white">{selectedWp.last_update_time}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Timestamp MS */}
                    <div>
                        <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Timestamp (MS)</p>
                        <p className="text-white font-mono text-sm">{selectedWp.creation_time_ms}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}